import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileUpload } from '@/components/ui/file-upload';
import {
  getLoginPhoto,
  createLoginPhoto,
  updateLoginPhoto,
  uploadLoginPhotoImage,
} from '@/api/login-photos';
import { getThemes } from '@/api/themes';
import type { LoginPhoto, LoginPhotoCreateData, Theme } from '@/types/login-photo';
import { ArrowLeft, Save, Image, Loader2 } from 'lucide-react';

export function LoginPhotoFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;

  // Determine back URL based on where user came from
  const from = searchParams.get('from');
  const backUrl = from === 'calendar' ? '/login-photos/calendar' : '/login-photos';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [themes, setThemes] = useState<Theme[]>([]);
  const [photo, setPhoto] = useState<LoginPhoto | null>(null);

  const [formData, setFormData] = useState<LoginPhotoCreateData>({
    title: '',
    comment: '',
    sourceLink: '',
    commentSourceLink: '',
    themeId: null,
    forcedDate: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load photo and themes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const themesData = await getThemes();
        setThemes(themesData);

        if (isEditing) {
          const photoData = await getLoginPhoto(Number(id));
          setPhoto(photoData);
          setFormData({
            title: photoData.title,
            comment: photoData.comment || '',
            sourceLink: photoData.sourceLink || '',
            commentSourceLink: photoData.commentSourceLink || '',
            themeId: photoData.theme?.id || null,
            forcedDate: photoData.forcedDate || '',
            isActive: photoData.isActive,
          });
        }
      } catch (err) {
        setError('Erreur lors du chargement');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditing]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  const handleThemeChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      themeId: value === 'none' ? null : Number(value),
    }));
  };

  const handleFileUpload = async (file: File) => {
    if (!photo) return;

    setUploading(true);
    setError(null);
    try {
      const result = await uploadLoginPhotoImage(photo.id, file);
      setPhoto((prev) =>
        prev
          ? {
              ...prev,
              thumbnailUrl: result.thumbnailUrl,
              photoUrl: result.photoUrl,
            }
          : null
      );
      setSuccess('Image uploadee avec succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de l\'upload de l\'image');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      const data: LoginPhotoCreateData = {
        title: formData.title.trim(),
        comment: formData.comment?.trim() || null,
        sourceLink: formData.sourceLink?.trim() || null,
        commentSourceLink: formData.commentSourceLink?.trim() || null,
        themeId: formData.themeId,
        forcedDate: formData.forcedDate || null,
        isActive: formData.isActive,
      };

      if (isEditing) {
        await updateLoginPhoto(Number(id), data);
        setSuccess('Photo mise a jour avec succes');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const result = await createLoginPhoto(data);
        navigate(`/login-photos/${result.id}/edit`);
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backUrl}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Image className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Modifier la photo' : 'Nouvelle photo'}
          </h1>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-700">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left column - Form fields */}
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Titre de la photo"
                  className={errors.title ? 'border-red-500' : ''}
                />
                {errors.title && (
                  <p className="text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="comment">Description</Label>
                <Textarea
                  id="comment"
                  name="comment"
                  value={formData.comment || ''}
                  onChange={handleChange}
                  placeholder="Description de la photo"
                  rows={3}
                />
              </div>

              {/* Theme */}
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select
                  value={formData.themeId?.toString() || 'none'}
                  onValueChange={handleThemeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner un theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun theme</SelectItem>
                    {themes.map((theme) => (
                      <SelectItem key={theme.id} value={theme.id.toString()}>
                        {theme.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Forced Date */}
              <div className="space-y-2">
                <Label htmlFor="forcedDate">Date forcee (optionnel)</Label>
                <Input
                  id="forcedDate"
                  name="forcedDate"
                  type="date"
                  value={formData.forcedDate || ''}
                  onChange={handleChange}
                />
                <p className="text-xs text-muted-foreground">
                  Si definie, la photo sera affichee uniquement a cette date.
                </p>
              </div>

              {/* Source links */}
              <div className="space-y-2">
                <Label htmlFor="sourceLink">Source de la photo</Label>
                <Input
                  id="sourceLink"
                  name="sourceLink"
                  value={formData.sourceLink || ''}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="commentSourceLink">Source du commentaire</Label>
                <Input
                  id="commentSourceLink"
                  name="commentSourceLink"
                  value={formData.commentSourceLink || ''}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Photo active</Label>
                  <p className="text-xs text-muted-foreground">
                    Les photos inactives ne sont pas affichees aux utilisateurs.
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={handleSwitchChange}
                />
              </div>
            </CardContent>
          </Card>

          {/* Right column - Image upload */}
          <Card>
            <CardHeader>
              <CardTitle>Image</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  {/* Current image preview */}
                  {photo?.photoUrl && (
                    <div className="relative rounded-lg overflow-hidden border">
                      <img
                        src={photo.photoUrl}
                        alt={photo.title}
                        className="w-full h-auto max-h-[400px] object-contain bg-gray-50"
                      />
                    </div>
                  )}

                  {/* File upload */}
                  <FileUpload
                    previewUrl={photo?.thumbnailUrl}
                    onChange={() => {}}
                    onUpload={handleFileUpload}
                    uploading={uploading}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    maxSize={30 * 1024 * 1024}
                    label="Changer l'image"
                    description="JPEG, PNG, GIF, WEBP (max 30MB). Taille recommandee: 2000x1333px"
                  />
                </div>
              ) : (
                <div className="text-center p-8 border-2 border-dashed rounded-lg bg-muted/50">
                  <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Enregistrez d'abord la photo pour pouvoir telecharger une image.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Submit button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" asChild>
            <Link to={backUrl}>Annuler</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? 'Enregistrer' : 'Creer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
